import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useEffectOnce from 'react-use/lib/useEffectOnce';

import {
  AuthFactoryProvider,
  LoadingOverlay,
  hasConfidenceAppSession,
} from '@spotify/confidence-core-react';
import {
  TrackingScope,
  withPageImpression,
} from '@spotify/plugin-confidence-tracking-react';

import { AccountCreated } from './AccountCreated';
import { CreateAccountForm } from './CreateAccountForm';
import { InviteError } from './InviteError';
import { CreatedAccount, useInvite, useRegionCheck } from './hooks';

const CLIENT_ID = '82qMvwZvqd3t3S0gRDvs8R53TehQXSJY';

const SignUpPageComponent = () => {
  const [token, setToken] = useState<string>();
  const [account, setAccount] = useState<CreatedAccount>();
  const { id: inviteId } = useParams();
  const navigate = useNavigate();
  const [{ error: inviteError, loading, value: inviteData }, getAccountInvite] =
    useInvite(inviteId);

  // Check user's region on page load
  const {
    loading: regionLoading,
    error: regionError,
    regionData,
  } = useRegionCheck();

  useEffectOnce(() => {
    (async () => {
      try {
        if (inviteId) {
          const invite = await getAccountInvite();
          setAccount(invite.createdAccount);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    })();
  });

  // Redirect to unsupported market page if region is not allowed
  useEffect(() => {
    // If region check fails, allow signup to proceed (fail open)
    if (regionError) {
      // eslint-disable-next-line no-console
      console.warn(
        'Region check failed, allowing signup to proceed:',
        regionError,
      );
      return;
    }

    if (!regionLoading && regionData?.allowed === false) {
      navigate('/signup/unsupported-market');
    }
  }, [regionLoading, regionError, regionData, navigate]);

  // Redirect users who already have a Confidence account to the app
  // (unless they have an invite link, in which case they might be joining a new workspace)
  if (!inviteId && hasConfidenceAppSession()) {
    return <Navigate to="/" replace />;
  }

  const accountDataAvailable = !loading && !!account;

  const renderContent = () => {
    // Wait for region check to complete before showing content
    if (loading || regionLoading) {
      return null;
    }
    if (inviteError) {
      return <InviteError error={inviteError} />;
    }
    if (accountDataAvailable) {
      return <AccountCreated createdAccount={account} />;
    }
    return (
      <AuthFactoryProvider
        clientId={CLIENT_ID}
        onTokenReceived={t => setToken(t)}
      >
        <TrackingScope elementId="main.signup">
          <CreateAccountForm
            inviteId={inviteId}
            token={token}
            onCreate={setAccount}
            skipCompanyWizard={inviteData?.warehouseNative ?? false}
          />
        </TrackingScope>
      </AuthFactoryProvider>
    );
  };

  return (
    <>
      <LoadingOverlay loading={loading || regionLoading} />
      {renderContent()}
    </>
  );
};

export const SignUpPage = withPageImpression(SignUpPageComponent, {
  pageId: 'signup',
});
